/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { PenTool, Upload, RefreshCw, Check, X, Shield, FileImage } from 'lucide-react';
import { compressImage } from '../utils/imageCompressor';

interface UserSignatureUpdaterProps {
  userName: string;
  currentSignature?: string;
  onSave: (signatureBase64: string) => void;
  onCancel: () => void;
}

export default function UserSignatureUpdater({
  userName,
  currentSignature,
  onSave,
  onCancel
}: UserSignatureUpdaterProps) {
  const [method, setMethod] = useState<'DRAW' | 'FILE'>('DRAW');
  const [signatureBase64, setSignatureBase64] = useState<string>(currentSignature || '');
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Drawing helpers
  const getCoordinates = (e: React.MouseEvent | React.TouchEvent | any) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    // Support mobile touch events
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    saveCanvasState();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureBase64('');
  };

  const saveCanvasState = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    const compressed = await compressImage(dataUrl, 300, 100);
    setSignatureBase64(compressed);
  };

  // Initialize canvas style when drawing mode is selected
  useEffect(() => {
    if (method === 'DRAW' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#0f172a'; // Slate-900 beautiful dark ink
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [method]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        if (typeof reader.result === 'string') {
          const compressed = await compressImage(reader.result, 300, 100);
          setSignatureBase64(compressed);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (!signatureBase64) return;
    onSave(signatureBase64);
  };

  return (
    <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 space-y-3 font-sans animate-fade-in text-slate-700">
      <div className="flex items-center justify-between border-b border-slate-150 pb-2">
        <span className="text-[11px] font-black uppercase text-slate-800 tracking-wider flex items-center gap-1">
          <Shield className="h-3.5 w-3.5 text-emerald-600" />
          Actualizar Firma de {userName}
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 p-0.5"
          title="Cerrar panel de firma"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex bg-slate-200/60 p-0.5 rounded-lg border border-slate-300/30">
        <button
          type="button"
          onClick={() => {
            setMethod('DRAW');
            setSignatureBase64('');
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all ${
            method === 'DRAW' ? 'bg-white text-slate-800 shadow-3xs' : 'text-slate-550'
          }`}
        >
          <PenTool className="h-3 w-3" />
          <span>Dibujar Firma</span>
        </button>
        <button
          type="button"
          onClick={() => {
            setMethod('FILE');
            setSignatureBase64('');
          }}
          className={`flex-1 flex items-center justify-center gap-1.5 py-1 rounded-md text-[10px] font-bold cursor-pointer transition-all ${
            method === 'FILE' ? 'bg-white text-slate-800 shadow-3xs' : 'text-slate-550'
          }`}
        >
          <Upload className="h-3 w-3" />
          <span>Cargar Imagen</span>
        </button>
      </div>

      {method === 'DRAW' ? (
        <div className="space-y-2">
          <p className="text-[10px] text-slate-450 leading-none">
            Dibuje su firma con el mouse o pantalla táctil en el recuadro gris:
          </p>
          <div className="relative border border-slate-300/80 rounded-lg overflow-hidden bg-slate-100 shadow-inner">
            <canvas
              ref={canvasRef}
              width={340}
              height={100}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              className="w-full block bg-slate-100/50 cursor-crosshair h-[100px]"
            />
            <button
              type="button"
              onClick={clearCanvas}
              className="absolute right-2 top-2 h-6.5 w-6.5 rounded-md bg-white hover:bg-slate-100 border border-slate-200 text-slate-600 flex items-center justify-center shadow-3xs transition-all cursor-pointer hover:border-slate-300"
              title="Limpiar firma"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] text-slate-450 leading-none">
            Suba un archivo de imagen transparente de preferencia:
          </p>
          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-white hover:bg-slate-50/50 transition-all">
            <div className="flex flex-col items-center justify-center pt-3 pb-3">
              <Upload className="w-5 h-5 mb-1.5 text-slate-400" />
              <p className="text-[10px] text-slate-550"><span className="font-bold">Elegir Archivo</span></p>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
        </div>
      )}

      {/* Signature Preview Thumbnail */}
      {signatureBase64 && (
        <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-150 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className="block text-[9px] font-black uppercase text-emerald-800 tracking-wider">Previsualización de la Firma</span>
            <div className="mt-1 bg-white/60 border border-emerald-100 rounded p-1 flex items-center justify-center max-h-[50px] overflow-hidden">
              <img
                src={signatureBase64}
                alt="Firma"
                className="max-h-[44px] object-contain"
                referrerPolicy="no-referrer"
              />
            </div>
          </div>
          <button
            type="button"
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] uppercase h-9 px-3 rounded-lg flex items-center gap-1 cursor-pointer transition-colors shadow-2xs shrink-0"
          >
            <Check className="h-3.5 w-3.5" />
            <span>Guardar</span>
          </button>
        </div>
      )}

      {!signatureBase64 && (
        <div className="text-center py-2 text-[10px] text-slate-400 italic">
          Por favor dibuje o cargue una firma para continuar.
        </div>
      )}
    </div>
  );
}
